const MAIN_PRODUCT_GALLERY_SELECTOR =
  '.shopify-section--main-product product-gallery media-carousel.product-gallery__media-list'
const SECTION_GALLERY_SELECTOR = 'product-gallery media-carousel.product-gallery__media-list'
const INTERACTIVE_SELECTOR =
  'a[href], button, input, select, textarea, video, model-viewer, [role="button"], [contenteditable="true"]'
const DRAG_THRESHOLD = 6
const SWIPE_DISTANCE = 80

const mountedCarousels = new WeakSet()
const installedRoots = new WeakSet()

function visibleSlides(carousel) {
  return Array.from(carousel.querySelectorAll(':scope > .product-gallery__media:not([hidden])'))
}

function nearestSlideIndex(slides, scrollLeft) {
  return slides.reduce((nearestIndex, slide, index) => {
    const nearestDistance = Math.abs(slides[nearestIndex].offsetLeft - scrollLeft)
    const currentDistance = Math.abs(slide.offsetLeft - scrollLeft)

    return currentDistance < nearestDistance ? index : nearestIndex
  }, 0)
}

function mountCarousel(carousel) {
  if (mountedCarousels.has(carousel)) return

  mountedCarousels.add(carousel)

  let active = false
  let pending = false
  let pointerId = null
  let startIndex = 0
  let startScrollLeft = 0
  let startX = 0
  let startY = 0
  let previousScrollBehavior = ''
  let previousScrollSnapType = ''
  let suppressClick = false
  let suppressClickTimer = null

  const syncDragAffordance = () => {
    const slides = visibleSlides(carousel)
    const enabled = slides.length > 1 && carousel.scrollWidth > carousel.clientWidth

    if (enabled) {
      carousel.dataset.productGalleryDrag = 'true'
    } else {
      delete carousel.dataset.productGalleryDrag
    }

    return { enabled, slides }
  }

  const resetPointer = () => {
    active = false
    pending = false
    pointerId = null
  }

  const restoreDraggingStyles = () => {
    carousel.classList.remove('is-product-gallery-dragging')
    carousel.style.scrollBehavior = previousScrollBehavior
    carousel.style.scrollSnapType = previousScrollSnapType
  }

  const releasePointer = (capturedPointerId) => {
    try {
      carousel.releasePointerCapture?.(capturedPointerId)
    } catch {
      // The browser may have released capture during cancellation or DOM replacement.
    }
  }

  const settle = (event, cancelled = false) => {
    const slides = visibleSlides(carousel)

    if (slides.length === 0) return

    const deltaX = event.clientX - startX
    let targetIndex = nearestSlideIndex(slides, carousel.scrollLeft)

    if (!cancelled && Math.abs(deltaX) >= SWIPE_DISTANCE) {
      const direction = deltaX < 0 ? 1 : -1
      targetIndex = Math.min(Math.max(startIndex + direction, 0), slides.length - 1)
    }

    carousel.scrollTo({
      behavior: 'smooth',
      left: slides[targetIndex].offsetLeft,
    })
  }

  const finishDrag = (event, cancelled = false) => {
    const capturedPointerId = pointerId

    restoreDraggingStyles()
    releasePointer(capturedPointerId)
    settle(event, cancelled)

    if (!cancelled) {
      suppressClick = true
      clearTimeout(suppressClickTimer)
      suppressClickTimer = setTimeout(() => {
        suppressClick = false
      }, 0)
    }

    resetPointer()
  }

  carousel.addEventListener('pointerdown', (event) => {
    const { enabled, slides } = syncDragAffordance()
    const startsOnControl = event.target.closest?.(INTERACTIVE_SELECTOR)

    if (
      event.pointerType === 'touch' ||
      event.button !== 0 ||
      event.isPrimary === false ||
      startsOnControl ||
      !enabled
    ) {
      return
    }

    pending = true
    pointerId = event.pointerId
    startX = event.clientX
    startY = event.clientY
    startScrollLeft = carousel.scrollLeft
    startIndex = nearestSlideIndex(slides, startScrollLeft)
  })

  carousel.addEventListener(
    'pointermove',
    (event) => {
      if (!pending || event.pointerId !== pointerId) return

      const deltaX = event.clientX - startX
      const deltaY = event.clientY - startY

      if (!active) {
        if (Math.abs(deltaY) > DRAG_THRESHOLD && Math.abs(deltaY) > Math.abs(deltaX)) {
          resetPointer()
          return
        }

        if (Math.abs(deltaX) <= DRAG_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
          return
        }

        active = true
        previousScrollBehavior = carousel.style.scrollBehavior
        previousScrollSnapType = carousel.style.scrollSnapType
        carousel.style.scrollBehavior = 'auto'
        carousel.style.scrollSnapType = 'none'
        carousel.classList.add('is-product-gallery-dragging')
        carousel.setPointerCapture?.(pointerId)
      }

      event.preventDefault()
      carousel.scrollLeft = startScrollLeft - deltaX
    },
    { passive: false },
  )

  carousel.addEventListener('pointerup', (event) => {
    if (!pending || event.pointerId !== pointerId) return

    if (active) {
      finishDrag(event)
    } else {
      resetPointer()
    }
  })

  carousel.addEventListener('pointercancel', (event) => {
    if (!pending || event.pointerId !== pointerId) return

    if (active) {
      finishDrag(event, true)
    } else {
      resetPointer()
    }
  })

  carousel.addEventListener('dragstart', (event) => {
    if (!event.target.closest?.(INTERACTIVE_SELECTOR)) event.preventDefault()
  })

  carousel.addEventListener(
    'click',
    (event) => {
      if (!suppressClick) return

      suppressClick = false
      clearTimeout(suppressClickTimer)
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    { capture: true },
  )

  carousel.addEventListener('carousel:filter', () => queueMicrotask(syncDragAffordance))

  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(syncDragAffordance).observe(carousel)
  }

  syncDragAffordance()
}

function galleriesWithin(root) {
  const carousels = Array.from(root.querySelectorAll(MAIN_PRODUCT_GALLERY_SELECTOR))

  if (root.matches?.('.shopify-section--main-product')) {
    carousels.push(...root.querySelectorAll(SECTION_GALLERY_SELECTOR))
  }

  return carousels
}

export function setupProductGalleryDrag(root = document) {
  galleriesWithin(root).forEach(mountCarousel)
}

export function installProductGalleryDrag(root = document) {
  setupProductGalleryDrag(root)

  if (installedRoots.has(root)) return

  installedRoots.add(root)
  root.addEventListener('shopify:section:load', (event) => {
    setupProductGalleryDrag(event.target)
  })
}

if (typeof document !== 'undefined') installProductGalleryDrag(document)
